/**
 * Valida um número de conta do banco Sicoob usando o dígito verificador (DV),
 * e garante que ele não contenha mais de 5 dígitos sequenciais
 * nem mais de 7 dígitos repetidos consecutivos.
 *
 * O número da conta deve conter exatamente 9 dígitos numéricos:
 * os 8 primeiros representam o número da conta propriamente dito,
 * e o último dígito é o dígito verificador (DV).
 *
 * O DV é calculado com base na soma ponderada dos 8 primeiros dígitos,
 * utilizando uma sequência fixa de multiplicadores (de 9 a 2), conforme as regras do Sicoob.
 *
 * @param rawCheckingAccountNumber - O número da conta como string (com até 9 dígitos).
 * Se tiver menos de 9 dígitos, será preenchido com zeros à esquerda.
 *
 * @returns `true` se o dígito verificador for válido e a conta não contiver mais de 5 dígitos sequenciais
 * ou 7 dígitos repetidos consecutivos, `false` caso contrário.
 *
 * @example
 * sicoobValidator("123456789"); // false (dígitos sequenciais demais)
 * sicoobValidator("123450012"); // true (DV válido e sem sequência longa)
 * sicoobValidator("123456781"); // false (DV incorreto)
 * sicoobValidator("000000012"); // false (mais de 7 dígitos repetidos)
 */

export const checkSicoobAccount = (
  rawCheckingAccountNumber: string
): boolean => {
  const MULTIPLIERS = [9, 8, 7, 6, 5, 4, 3, 2];

  const paddedCheckingAccountNumber = rawCheckingAccountNumber.padStart(9, "0");
  const SEQUENTIAL_DIGITS_LIMIT = 5;
  const REPEATED_DIGITS_LIMIT = 7;

  if (
    hasTooManySequentialDigits(
      paddedCheckingAccountNumber,
      SEQUENTIAL_DIGITS_LIMIT
    )
  ) {
    return false;
  }

  if (
    hasTooManyRepeatedDigits(paddedCheckingAccountNumber, REPEATED_DIGITS_LIMIT)
  ) {
    return false;
  }

  const digitArray = Array.from(paddedCheckingAccountNumber, char =>
    Number(char)
  );
  const actualCheckDigit = digitArray.pop();

  const sumOfWeightedDigits = digitArray.reduce(
    (total, digit, index) => total + digit * MULTIPLIERS[index],
    0
  );

  const remainder = sumOfWeightedDigits % 11;
  const expectedCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  return expectedCheckDigit === actualCheckDigit;
};

/**
 * Verifica se uma sequência de dígitos contém mais do que o número permitido (`limit`)
 * de dígitos sequenciais em ordem crescente ou decrescente.
 *
 * @param digits - A string contendo os dígitos a serem verificados
 * @param limit - Número máximo permitido de dígitos sequenciais (ex.: 5)
 * @returns `true` se a sequência exceder o limite, caso contrário `false`
 *
 * @example
 * hasTooManySequentialDigits("1234567", 5); // true (7 dígitos crescentes)
 * hasTooManySequentialDigits("9876543", 5); // true (7 dígitos decrescentes)
 */
function hasTooManySequentialDigits(digits: string, limit: number): boolean {
  let ascCount = 1;
  let descCount = 1;

  for (let i = 1; i < digits.length; i++) {
    const current = Number(digits[i]);
    const prev = Number(digits[i - 1]);

    if (current === prev + 1) {
      ascCount++;

      if (ascCount > limit) return true;
    } else {
      ascCount = 1;
    }

    if (current === prev - 1) {
      descCount++;

      if (descCount > limit) return true;
    } else {
      descCount = 1;
    }
  }

  return false;
}

/**
 * Verifica se uma sequência de dígitos contém mais do que o número permitido (`limit`)
 * de dígitos repetidos consecutivamente.
 *
 * @param digits - A string contendo os dígitos a serem verificados
 * @param limit - Número máximo permitido de dígitos repetidos consecutivos (ex.: 7)
 * @returns `true` se a sequência exceder o limite, caso contrário `false`
 *
 * @example
 * hasTooManyRepeatedDigits("00000000", 7); // true (8 zeros)
 * hasTooManyRepeatedDigits("1111111", 7);  // false (7 uns)
 */
function hasTooManyRepeatedDigits(digits: string, limit: number): boolean {
  let repeatCount = 1;

  for (let i = 1; i < digits.length; i++) {
    if (digits[i] === digits[i - 1]) {
      repeatCount++;

      if (repeatCount > limit) return true;
    } else {
      repeatCount = 1;
    }
  }

  return false;
}
